import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/admin/AuthContext";

/**
 * Reads the `?preview=draft` URL search param.
 * Updates whenever the router location changes, including hydration & navigation.
 */
export function useIsDraftPreview(): boolean {
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });
  const [active, setActive] = useState(false);

  useEffect(() => {
    const fromRouter = search?.preview === "draft";
    let fromUrl = false;
    if (typeof window !== "undefined") {
      try {
        fromUrl = new URL(window.location.href).searchParams.get("preview") === "draft";
      } catch {
        fromUrl = false;
      }
    }
    setActive(Boolean(fromRouter) || fromUrl);
  }, [search]);

  return active;
}

/**
 * True when the URL requests draft preview AND the current user is staff
 * (admin or editor). Non-staff visitors silently fall back to published content.
 */
export function useDraftPreviewAllowed(): {
  enabled: boolean;
  requested: boolean;
  isStaff: boolean;
  loading: boolean;
} {
  const requested = useIsDraftPreview();
  const { isStaff, loading } = useAuth();
  return {
    requested,
    isStaff,
    loading,
    enabled: requested && isStaff,
  };
}

interface PageRow {
  id: string;
  slug: string;
  status: "draft" | "published";
  content: Record<string, unknown>;
  draft_content: Record<string, unknown> | null;
  updated_at: string;
}

/**
 * Loads a page by slug from the database. When draft preview is enabled
 * and the user is staff, returns `draft_content` (falling back to `content`
 * when no draft exists). Otherwise always returns `content`.
 */
export function usePageContent(slug: string) {
  const { enabled: previewEnabled } = useDraftPreviewAllowed();

  const query = useQuery({
    queryKey: ["public", "page", slug, previewEnabled ? "draft" : "published"],
    queryFn: async (): Promise<PageRow | null> => {
      const { data, error } = await supabase
        .from("pages")
        .select("id,slug,status,content,draft_content,updated_at")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        slug: data.slug,
        status: data.status,
        content: (data.content as Record<string, unknown>) ?? {},
        draft_content: (data.draft_content as Record<string, unknown> | null) ?? null,
        updated_at: data.updated_at,
      };
    },
    staleTime: previewEnabled ? 0 : 60_000,
  });

  const page = query.data ?? null;
  const content =
    previewEnabled && page?.draft_content ? page.draft_content : page?.content ?? null;

  return {
    ...query,
    page,
    content,
    isDraftPreview: previewEnabled,
    hasDraft: !!page?.draft_content,
  };
}
